import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import { Close, Email } from "@mui/icons-material";
import fetchSingleApplicant from "layouts/pages/applicants/actions/fetchSingleApplicant";
import { useSnackbar } from "notistack";
import { useAppContextController } from "context/AppContext";
import SendMessage from "components/SendMessage";
import CircularLoader from "components/CircularLoader";
import { ApplicantsTable } from "layouts/pages/events/components/BulkMessagingModal/ApplicantsTable";
import moment from "moment-timezone";

const BulkMessageShiftModal = ({
  open,
  onClose,
  shift,
  job,
  allStaffingPool,
  isStaffingPoolLoading,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { companyType } = useAppContextController();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [allRows, setRows] = useState([]);
  const [text, setText] = useState(false);
  const [email, setEmail] = useState(true);
  const [system, setSystem] = useState(false);
  const [recipient, setRecipient] = useState(null);
  const [pushNotificationSelection, setPushNotificationSelection] = useState(null);

  // Filter staffing pool to get only shift employees
  const applicants = useMemo(() => {
    if (!shift || !allStaffingPool) return [];

    // Get applicants assigned to this specific shift
    const shiftRosterIds =
      shift.shiftRoster?.map((roster) => (typeof roster === "string" ? roster : roster._id)) || [];

    if (shiftRosterIds.length === 0) return [];

    // Filter from existing staffing pool
    return allStaffingPool.filter((employee) => shiftRosterIds.includes(employee._id));
  }, [shift, allStaffingPool]);

  // Get recipient for validity check
  const getRecipientForValidity = (propVal) => {
    let tempId;
    selectedRecipients.forEach((selRec) => {
      const row = allRows.find((app) => !app[propVal] && app._id === selRec);
      if (row) tempId = row._id;
    });
    return tempId;
  };

  // Set recipient
  useEffect(() => {
    const getFirstRecipient = async (id) => {
      try {
        const rec = await fetchSingleApplicant({ applicantId: id });
        if (rec?.data) setRecipient(rec.data);
      } catch (error) {
        console.error("Error fetching recipient:", error);
      }
    };

    if (selectedRecipients?.length) {
      const rec = selectedRecipients[0];
      let emailId;
      let phoneId;
      if (email === false) emailId = getRecipientForValidity("email");
      if (text === false) phoneId = getRecipientForValidity("phone");

      if ((!emailId && phoneId) || (emailId && !phoneId)) {
        const id = emailId || phoneId;
        getFirstRecipient(id);
      }
      if (emailId && phoneId) setRecipient({ id: emailId });
      if (!emailId && !phoneId) {
        getFirstRecipient(rec);
      }
    } else {
      setRecipient(null);
    }
  }, [selectedRecipients, text, email, allRows]);

  // Disable button check
  const disableButton = React.useMemo(() => {
    if (pushNotificationSelection === "selected-individuals") {
      return !selectedRecipients.length || !recipient;
    }
    return (!selectedRecipients.length || !recipient) && !pushNotificationSelection;
  }, [selectedRecipients, recipient, pushNotificationSelection]);

  // Handle sending the message
  const handleSend = async () => {
    if (!shift || selectedRecipients.length === 0) {
      enqueueSnackbar("Please select at least one employee", { variant: "warning" });
      return;
    }

    try {
      setIsLoading(true);

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      enqueueSnackbar(`Message sent to ${selectedRecipients.length} employees successfully`, {
        variant: "success",
      });
      onClose();
    } catch (error) {
      enqueueSnackbar("Failed to send message to employees", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Create recipients table component
  const recipientsTable = (
    <ApplicantsTable
      applicants={applicants}
      setRows={setRows}
      selectedRecipients={selectedRecipients}
      setSelectedRecipients={setSelectedRecipients}
      emailChecked={email}
      smsChecked={text}
      systemChecked={system}
      isLoading={isStaffingPoolLoading}
    />
  );

  if (!shift) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: "1200px",
          width: "95vw",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            <Email sx={{ mr: 1, verticalAlign: "middle" }} />
            Bulk Message: {shift.shiftName}
          </Typography>
          <Button onClick={onClose} color="inherit" size="medium" sx={{ minWidth: "auto" }}>
            <Close />
          </Button>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {isLoading ? (
          <Box sx={{ height: "100px", display: "relative" }}>
            <CircularLoader isLoading color="info" hasBackdrop={false} />
          </Box>
        ) : (
          <SendMessage
            recipient={recipient || { _id: "", email: "", phone: "", firstName: "", lastName: "" }}
            companyType={companyType}
            setOpen={onClose}
            text={text}
            setText={setText}
            email={email}
            setEmail={setEmail}
            system={system}
            setSystem={setSystem}
            isButtonDisabled={disableButton}
            bulk
            selectedRecipients={selectedRecipients}
            messageContext="Applicant"
            attachmentContext="Shift"
            pushNotificationSelection={pushNotificationSelection}
            setPushNotificationSelection={setPushNotificationSelection}
            recipientsTable={recipientsTable}
            templateIn="Shift Assignment Notification"
            templateSelectionDisabled
            noText
            isPanel
            jobId={job?._id}
            applicants={applicants}
            shiftInfo={{
              slug: shift.slug,
              shiftName: shift.shiftName,
              startDate: `${moment(shift.shiftStartDate)
                .tz("America/Chicago")
                .format("MM/DD/YYYY")} America/Chicago(CT)`,
              endDate: `${moment(shift.shiftEndDate)
                .tz("America/Chicago")
                .format("MM/DD/YYYY")} America/Chicago(CT)`,
              schedule: ``,
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

BulkMessageShiftModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  shift: PropTypes.object,
  job: PropTypes.object,
  allStaffingPool: PropTypes.array,
  isStaffingPoolLoading: PropTypes.bool,
};

BulkMessageShiftModal.defaultProps = {
  shift: null,
  job: null,
  allStaffingPool: [],
  isStaffingPoolLoading: false,
};

export default BulkMessageShiftModal;
